/**
 * Infinite scroll for the collection grid.
 *
 * Loads the next page when the visitor nears the bottom of the grid, and appends
 * its product cards. The numbered pagination stays in the markup as the no-JS
 * fallback and is hidden only once we know there is a next page to load.
 *
 * Why this was rewritten:
 *  - The next page was found via `.pagination__item--next`, but the pagination
 *    snippet (from Dawn) puts that class on the PREVIOUS link. On page 1 there is
 *    no previous link, so it concluded there were no more pages and left the
 *    pagination visible. It now reads `data-next-url` from the wrapper.
 *  - It fetched the WHOLE next page — header, footer, a ~440 KB analytics script —
 *    and parsed it in memory to pull the cards out. On phones already near their
 *    memory limit that was part of the crash. It now requests only this section
 *    via Shopify's section rendering (`section_id`).
 *  - Filtering re-renders #ProductGridContainer, which detached everything the old
 *    instance referenced, and the event it waited for to recover was never fired.
 *    It now watches the container and rebuilds itself when that happens.
 *
 * Card behaviour on appended cards (gallery, quick add) needs nothing here: those
 * scripts use MutationObservers and document-level delegation.
 */
(function () {
  'use strict';

  var instance = null;

  // How far above the bottom the next batch starts loading. Small, so products
  // arrive as the visitor reaches the end rather than well before it — one place
  // to change if it wants tuning either way.
  var PRELOAD_MARGIN = 50;

  function InfiniteScroll(gridContainer) {
    this.gridContainer = gridContainer;
    this.grid = gridContainer.querySelector('#product-grid');
    this.pagination = gridContainer.querySelector('.pagination-wrapper');
    this.sectionId = this.grid ? this.grid.getAttribute('data-id') : null;
    this.nextUrl = this.pagination ? this.pagination.getAttribute('data-next-url') : null;
    this.loading = false;
    this.controller = null;
    this.observer = null;
    this.sentinel = null;

    if (!this.grid || !this.sectionId || !this.nextUrl) {
      // Silent failure here is what made this hard to diagnose: say which piece is
      // missing so it can be seen in the console rather than guessed at.
      console.warn('[infinite-scroll] not starting —',
        'grid:', !!this.grid,
        'sectionId:', this.sectionId,
        'pagination:', !!this.pagination,
        'nextUrl:', this.nextUrl);
      return;
    }

    this.build();
  }

  InfiniteScroll.prototype.build = function () {
    var self = this;
    // Inline style rather than the hidden attribute: the wrapper's own display rule
    // would out-rank [hidden] and leave the numbers on screen.
    this.pagination.style.display = 'none';

    // The trigger and the spinner must be separate elements. The spinner's class is
    // display:none until it is active, and an element with no layout box never
    // intersects — observing it meant the observer never fired and nothing loaded.
    this.status = document.createElement('div');
    this.status.className = 'infinite-scroll-loading';
    this.status.setAttribute('aria-live', 'polite');
    this.status.innerHTML =
      '<div class="loading-overlay__spinner">' +
      '<svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
      '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div>' +
      '<span class="infinite-scroll-loading__text">Loading more products…</span>';

    this.sentinel = document.createElement('div');
    this.sentinel.className = 'infinite-scroll-sentinel';
    this.sentinel.setAttribute('aria-hidden', 'true');

    this.pagination.parentNode.insertBefore(this.status, this.pagination);
    this.pagination.parentNode.insertBefore(this.sentinel, this.status);

    // Start a little before the very bottom so the next batch is usually in place
    // by the time the visitor gets there, rather than making them wait at the edge.
    this.observer = new IntersectionObserver(function (entries) {
      if (entries[0] && entries[0].isIntersecting) self.load();
    }, { rootMargin: '0px 0px ' + PRELOAD_MARGIN + 'px 0px' });
    this.observer.observe(this.sentinel);

    // Scroll fallback. If the observer never fires — a hidden ancestor, an unusual
    // scroll container, an older browser — this still loads. Cheap: one rAF-guarded
    // measurement per scroll, and it stops once loading finishes.
    var ticking = false;
    this.onScroll = function () {
      if (ticking || self.loading || !self.nextUrl) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        if (!self.sentinel) return;
        var rect = self.sentinel.getBoundingClientRect();
        if (rect.top <= (window.innerHeight || 0) + PRELOAD_MARGIN) self.load();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });

    this.fillRows();

    // The grid-layout selector swaps cpg-mobile-N / cpg-desktop-N classes on the
    // grid, and crossing a breakpoint changes the column count too. Either way the
    // last row may change shape, so re-measure.
    this.classWatcher = new MutationObserver(function () { self.fillRows(); });
    this.classWatcher.observe(this.grid, { attributes: true, attributeFilter: ['class'] });

    var resizeTimer;
    this.onResize = function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () { self.fillRows(); }, 150);
    };
    window.addEventListener('resize', this.onResize);
  };

  /**
   * Keep every visible row full while more products are still to come.
   *
   * A fixed batch size can't do this: filling rows for 1, 2 and 3 per row on
   * mobile AND 3, 4 and 5 on desktop needs a multiple of 60, which is heavier than
   * the batches that stop phones crashing — and hidden or early-access products are
   * skipped after the page is counted, so a 'page of 60' can still render 57.
   *
   * Instead the incomplete last row is held back until the next batch lands, at
   * which point it rejoins the grid and the new last row is measured. Columns are
   * read from the rendered layout rather than from the switcher's classes, so this
   * stays right for any view, breakpoint, or item spanning several columns. Once
   * there is nothing left to load, everything is shown — the final row of a
   * collection is whatever size the collection is.
   */
  InfiniteScroll.prototype.fillRows = function () {
    var grid = this.grid;
    if (!grid) return;

    var held = grid.querySelectorAll('.is-row-holdback');
    for (var i = 0; i < held.length; i++) held[i].classList.remove('is-row-holdback');

    if (!this.nextUrl) return;

    var tracks = window.getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
    if (tracks < 2) return;

    var items = Array.prototype.filter.call(grid.children, function (el) {
      return el.offsetParent !== null;
    });
    if (!items.length) return;

    var lastTop = -Infinity;
    items.forEach(function (el) {
      var top = Math.round(el.getBoundingClientRect().top);
      if (top > lastTop) lastTop = top;
    });

    var lastRow = items.filter(function (el) {
      return Math.round(el.getBoundingClientRect().top) === lastTop;
    });

    var used = 0;
    lastRow.forEach(function (el) {
      var span = /span\s+(\d+)/.exec(window.getComputedStyle(el).gridColumnEnd);
      used += span ? parseInt(span[1], 10) : 1;
    });

    if (used < tracks) {
      lastRow.forEach(function (el) { el.classList.add('is-row-holdback'); });
    }
  };

  InfiniteScroll.prototype.sectionUrl = function (url) {
    var u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', this.sectionId);
    return u.toString();
  };

  InfiniteScroll.prototype.load = function () {
    if (this.loading || !this.nextUrl) return;
    this.loading = true;
    this.status.classList.add('infinite-scroll-loading--active');
    this.status.setAttribute('data-loading', 'true');

    var self = this;
    this.controller = new AbortController();

    fetch(this.sectionUrl(this.nextUrl), { signal: this.controller.signal })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var items = doc.querySelectorAll('#product-grid > .collection-grid-item');
        var fragment = document.createDocumentFragment();
        items.forEach(function (item) {
          fragment.appendChild(document.importNode(item, true));
        });
        self.grid.appendChild(fragment);

        var nextWrapper = doc.querySelector('.pagination-wrapper');
        self.nextUrl = nextWrapper ? nextWrapper.getAttribute('data-next-url') : null;

        self.fillRows();

        if (typeof window.initializeScrollAnimationTrigger === 'function') {
          window.initializeScrollAnimationTrigger(self.grid);
        }
        document.dispatchEvent(new CustomEvent('infinite-scroll:loaded', { detail: { grid: self.grid, count: items.length } }));

        self.loading = false;
        self.status.classList.remove('infinite-scroll-loading--active');
        self.status.removeAttribute('data-loading');

        if (!self.nextUrl || items.length === 0) {
          self.finish();
          return;
        }

        // An IntersectionObserver only fires on a change, so if the trigger is still
        // on screen after a batch it never re-fires and loading stalls. But checking
        // immediately loaded the whole collection in one go: the cards are appended
        // before their images have sized, so the grid has barely grown, the trigger
        // still measures as on-screen, and each batch triggers the next.
        //
        // So: wait for layout to settle, then continue only if the trigger is
        // genuinely still within the viewport — not the pre-load margin. Otherwise
        // the next batch waits for the visitor to scroll.
        window.setTimeout(function () { self.continueIfVisible(); }, 500);
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return;
        console.warn('[infinite-scroll] load failed:', err);
        self.loading = false;
        self.status.classList.remove('infinite-scroll-loading--active');
        self.fail();
      });
  };

  InfiniteScroll.prototype.continueIfVisible = function () {
    if (this.loading || !this.nextUrl || !this.sentinel) return;
    // Strictly on screen, with no pre-load margin: this only exists to rescue a
    // batch too short to push the trigger out of view. Scrolling is what should
    // normally pull in the next one.
    var rect = this.sentinel.getBoundingClientRect();
    if (rect.top <= (window.innerHeight || 0)) this.load();
  };

  InfiniteScroll.prototype.fail = function () {
    var self = this;
    // If loading fails, don't leave the held-back row hidden behind a retry button.
    var held = this.grid.querySelectorAll('.is-row-holdback');
    for (var i = 0; i < held.length; i++) held[i].classList.remove('is-row-holdback');
    this.observer.disconnect();
    this.status.classList.add('infinite-scroll-loading--active');
    this.status.innerHTML =
      '<p class="infinite-scroll-error">Couldn’t load more products. <button type="button" class="link">Try again</button></p>';
    this.status.querySelector('button').addEventListener('click', function () {
      self.status.innerHTML = '<span class="infinite-scroll-loading__text">Loading more products…</span>';
      self.observer.observe(self.sentinel);
      self.load();
    });
  };

  InfiniteScroll.prototype.finish = function () {
    if (this.observer) this.observer.disconnect();
    if (this.sentinel) this.sentinel.remove();
    if (this.status) this.status.remove();
    if (this.classWatcher) this.classWatcher.disconnect();
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    if (this.grid) {
      var held = this.grid.querySelectorAll('.is-row-holdback');
      for (var i = 0; i < held.length; i++) held[i].classList.remove('is-row-holdback');
    }
  };

  InfiniteScroll.prototype.destroy = function () {
    if (this.controller) this.controller.abort();
    this.finish();
  };

  function init() {
    var container = document.getElementById('ProductGridContainer');
    if (!container || container.getAttribute('data-infinite-scroll') !== 'true') return;

    if (instance) instance.destroy();
    instance = new InfiniteScroll(container);
    window.infiniteScroll = instance;

    // Filtering and sorting replace this container's contents wholesale. Rebuild on
    // that, rather than waiting for an event nothing dispatches.
    if (!container.__infiniteScrollWatched) {
      container.__infiniteScrollWatched = true;
      new MutationObserver(function (mutations) {
        var replaced = mutations.some(function (m) {
          return Array.prototype.some.call(m.addedNodes, function (n) {
            return n.nodeType === 1 && (n.id === 'product-grid' || (n.querySelector && n.querySelector('#product-grid')));
          });
        });
        if (replaced) init();
      }).observe(container, { childList: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector('#ProductGridContainer')) init();
  });
})();
